/**
 * 视口变换与交互（M1-13 / DESIGN.md §4.1、§7.4）。
 *
 * 视口把**文档坐标**映射到**屏幕坐标**：先绕文档原点缩放与旋转，再平移。
 * 两套坐标全程分开命名，函数名里带 `documentTo` / `screenTo`——
 * 混用这两者是这类代码最常见的 bug，而且症状（画面偏一点、缩放中心跑掉）
 * 看起来像是"手感问题"，不像是错误。
 *
 * 本模块是**纯计算**：没有 DOM、没有事件监听、没有 GPU。交互被建模成
 * 「当前视口 + 一个输入 → 新视口」的纯函数，于是所有手感相关的行为
 * （缩放锚点、抓手、适应窗口）都能在没有浏览器的情况下断言。
 *
 * 分层：interaction 层在 app 之下、document 之上，不得引用 engine/gpu。
 * 这里连那两层的类型都没出现。
 */

/** 屏幕坐标（CSS 像素，原点在画布区左上角）。 */
export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** 文档坐标（文档像素，原点在文档左上角）。 */
export interface DocumentPoint {
  readonly x: number;
  readonly y: number;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * 视口状态。
 *
 * `scale` 是每个文档像素占多少屏幕像素；`rotation` 是弧度，正方向为顺时针
 * （与屏幕坐标系 y 轴向下一致）；`tx`/`ty` 是缩放旋转**之后**的平移量，单位是屏幕像素。
 */
export interface Viewport {
  readonly scale: number;
  readonly rotation: number;
  readonly tx: number;
  readonly ty: number;
}

/**
 * 缩放上下限。
 *
 * 跟随 Photoshop：最小 1%，最大 3200%。不设上限的话，用户滚几下滚轮就会
 * 让浮点精度崩掉，画面开始抖。
 */
export const MIN_SCALE = 0.01;
export const MAX_SCALE = 32;

export const IDENTITY_VIEWPORT: Viewport = { scale: 1, rotation: 0, tx: 0, ty: 0 };

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/* ------------------------------------------------------------------ */
/* 坐标变换                                                             */
/* ------------------------------------------------------------------ */

export function documentToScreen(viewport: Viewport, point: DocumentPoint): ScreenPoint {
  const { scale, rotation, tx, ty } = viewport;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const sx = point.x * scale;
  const sy = point.y * scale;
  return { x: sx * cos - sy * sin + tx, y: sx * sin + sy * cos + ty };
}

/**
 * 屏幕坐标反算文档坐标。
 *
 * 必须是 {@link documentToScreen} 的**精确**逆运算——缩放锚点、拾色、
 * 画笔落点全都依赖它。近似的逆运算会让"以光标为中心缩放"慢慢漂移。
 */
export function screenToDocument(viewport: Viewport, point: ScreenPoint): DocumentPoint {
  const { scale, rotation, tx, ty } = viewport;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const dx = point.x - tx;
  const dy = point.y - ty;
  // 逆旋转
  const rx = dx * cos + dy * sin;
  const ry = -dx * sin + dy * cos;
  return { x: rx / scale, y: ry / scale };
}

/* ------------------------------------------------------------------ */
/* 交互                                                                 */
/* ------------------------------------------------------------------ */

/** 平移（抓手工具、空格拖拽、双指滑动）。单位是屏幕像素。 */
export function panBy(viewport: Viewport, dx: number, dy: number): Viewport {
  return { ...viewport, tx: viewport.tx + dx, ty: viewport.ty + dy };
}

/**
 * 以某个屏幕点为锚点缩放。
 *
 * 「锚点」的意思是：缩放前后，该屏幕点下面的**文档像素不变**。
 * 做法是先记下锚点对应的文档坐标，改完 scale 后反推需要多少平移量
 * 才能让它回到原来的屏幕位置——而不是去调整 tx/ty 的比例，
 * 后者在有旋转时会算错。
 */
export function zoomAt(viewport: Viewport, anchor: ScreenPoint, nextScale: number): Viewport {
  const scale = clampScale(nextScale);
  if (scale === viewport.scale) return viewport;

  const docPoint = screenToDocument(viewport, anchor);
  const scaled: Viewport = { ...viewport, scale };
  const after = documentToScreen(scaled, docPoint);
  return { ...scaled, tx: scaled.tx + (anchor.x - after.x), ty: scaled.ty + (anchor.y - after.y) };
}

/** 按比例缩放（滚轮一格、Ctrl +/-）。 */
export function zoomByFactor(viewport: Viewport, anchor: ScreenPoint, factor: number): Viewport {
  return zoomAt(viewport, anchor, viewport.scale * factor);
}

/** 以某个屏幕点为锚点旋转。与 {@link zoomAt} 同样的锚点保持逻辑。 */
export function rotateAt(viewport: Viewport, anchor: ScreenPoint, nextRotation: number): Viewport {
  const docPoint = screenToDocument(viewport, anchor);
  const rotated: Viewport = { ...viewport, rotation: normalizeAngle(nextRotation) };
  const after = documentToScreen(rotated, docPoint);
  return {
    ...rotated,
    tx: rotated.tx + (anchor.x - after.x),
    ty: rotated.ty + (anchor.y - after.y),
  };
}

/** 把角度归一到 [0, 2π)。累加旋转不归一的话，数值会越滚越大。 */
export function normalizeAngle(radians: number): number {
  const tau = Math.PI * 2;
  const r = radians % tau;
  return r < 0 ? r + tau : r;
}

/* ------------------------------------------------------------------ */
/* 视图命令                                                             */
/* ------------------------------------------------------------------ */

/** 适应窗口（Ctrl+0）：整个文档可见并居中，留一点边距。 */
export function fitToWindow(document: Size, view: Size, padding = 16): Viewport {
  if (document.width <= 0 || document.height <= 0 || view.width <= 0 || view.height <= 0) {
    return IDENTITY_VIEWPORT;
  }
  const available = {
    width: Math.max(1, view.width - padding * 2),
    height: Math.max(1, view.height - padding * 2),
  };
  // 取两个方向里更小的比例，保证整个文档都进得来
  const scale = clampScale(
    Math.min(available.width / document.width, available.height / document.height),
  );
  return centerAt(document, view, { scale, rotation: 0, tx: 0, ty: 0 });
}

/**
 * 实际像素（Ctrl+1）：缩放回 100%，保持画面中心对应的文档位置不变。
 *
 * Photoshop 的行为是「围绕视图中心」而不是回到文档原点——后者会让用户
 * 在放大观察局部时按一下 Ctrl+1 就完全迷失位置。
 */
export function actualPixels(viewport: Viewport, view: Size): Viewport {
  const center: ScreenPoint = { x: view.width / 2, y: view.height / 2 };
  return zoomAt(viewport, center, 1);
}

/** 把文档摆到视图正中，保持当前缩放与旋转。 */
export function centerAt(document: Size, view: Size, viewport: Viewport): Viewport {
  const corners: DocumentPoint[] = [
    { x: 0, y: 0 },
    { x: document.width, y: 0 },
    { x: document.width, y: document.height },
    { x: 0, y: document.height },
  ];
  const noTranslate: Viewport = { ...viewport, tx: 0, ty: 0 };
  const projected = corners.map((c) => documentToScreen(noTranslate, c));
  const minX = Math.min(...projected.map((p) => p.x));
  const maxX = Math.max(...projected.map((p) => p.x));
  const minY = Math.min(...projected.map((p) => p.y));
  const maxY = Math.max(...projected.map((p) => p.y));

  return {
    ...viewport,
    tx: (view.width - (maxX - minX)) / 2 - minX,
    ty: (view.height - (maxY - minY)) / 2 - minY,
  };
}

/** 文档在屏幕上的包围盒（考虑旋转）。滚动条与"是否需要重绘"都用它。 */
export function documentBoundsOnScreen(
  document: Size,
  viewport: Viewport,
): { x: number; y: number; width: number; height: number } {
  const corners: DocumentPoint[] = [
    { x: 0, y: 0 },
    { x: document.width, y: 0 },
    { x: document.width, y: document.height },
    { x: 0, y: document.height },
  ];
  const projected = corners.map((c) => documentToScreen(viewport, c));
  const xs = projected.map((p) => p.x);
  const ys = projected.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

/**
 * 屏幕视口在**文档空间**的外接矩形，裁到文档范围内（M3-10）。
 *
 * GPU 执行器按文档像素矩形工作（`scheduleTiles` 的 `Viewport`），而交互层的
 * `Viewport` 是屏幕空间的 T·R·S——两者不是同一回事，这是两者之间的转换。
 * 旋转视口下屏幕矩形反算回文档空间也是个矩形，但四角分别反算再取外接盒
 * （而不是直接用 documentBoundsOnScreen 的逆）：旋转时文档在屏幕上的可见区域
 * 是文档轴对齐矩形反算出的一个斜矩形的外接盒，两次转换不能简单对调顺序。
 */
export function documentViewportRect(
  document: Size,
  viewport: Viewport,
  view: Size,
): { x: number; y: number; width: number; height: number } {
  const corners: ScreenPoint[] = [
    { x: 0, y: 0 },
    { x: view.width, y: 0 },
    { x: view.width, y: view.height },
    { x: 0, y: view.height },
  ];
  const projected = corners.map((c) => screenToDocument(viewport, c));
  const xs = projected.map((p) => p.x);
  const ys = projected.map((p) => p.y);
  const x0 = Math.max(0, Math.floor(Math.min(...xs)));
  const y0 = Math.max(0, Math.floor(Math.min(...ys)));
  const x1 = Math.min(document.width, Math.ceil(Math.max(...xs)));
  const y1 = Math.min(document.height, Math.ceil(Math.max(...ys)));
  return { x: x0, y: y0, width: Math.max(0, x1 - x0), height: Math.max(0, y1 - y0) };
}

/**
 * 视口对应的文档可见区域（轴对齐包围盒，文档像素）。
 *
 * 交给 M1-10 的调度器做视口剔除。有旋转时取包围盒会多渲染一些块，
 * 但那是正确的保守做法——少渲染会露白。
 */
export function visibleDocumentRect(
  viewport: Viewport,
  view: Size,
): { x: number; y: number; width: number; height: number } {
  const corners: ScreenPoint[] = [
    { x: 0, y: 0 },
    { x: view.width, y: 0 },
    { x: view.width, y: view.height },
    { x: 0, y: view.height },
  ];
  const projected = corners.map((c) => screenToDocument(viewport, c));
  const xs = projected.map((p) => p.x);
  const ys = projected.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

/* ------------------------------------------------------------------ */
/* 输入映射（§7.4：快捷键跟随 Photoshop）                                 */
/* ------------------------------------------------------------------ */

/** 视图命令。UI 层把按键翻译成这些，再交给下面的 reducer。 */
export type ViewCommand =
  | { readonly kind: 'fit' }
  | { readonly kind: 'actualPixels' }
  | { readonly kind: 'zoomIn'; readonly anchor?: ScreenPoint }
  | { readonly kind: 'zoomOut'; readonly anchor?: ScreenPoint }
  /** 连续缩放（滚轮/触控板捏合）：`factor` 直接是要乘的倍率，不是固定档位。 */
  | { readonly kind: 'zoomBy'; readonly factor: number; readonly anchor?: ScreenPoint }
  | { readonly kind: 'pan'; readonly dx: number; readonly dy: number }
  | { readonly kind: 'rotate'; readonly radians: number; readonly anchor?: ScreenPoint }
  | { readonly kind: 'resetRotation' };

/** 每按一次 Ctrl +/- 的缩放倍率。 */
export const ZOOM_STEP = Math.SQRT2;

/**
 * 连续缩放（滚轮/触控板捏合）的灵敏度换算。
 *
 * 单个鼠标滚轮"格"的 `deltaY` 幅度多数浏览器约是 100——取这个基准，
 * 让鼠标滚轮一格的缩放手感跟原来（固定 `ZOOM_STEP`）基本一致。触控板
 * 双指捏合每帧派发的 wheel 事件密集得多、单次 `deltaY` 通常小得多，
 * 之前不管幅度大小都套一个固定 `ZOOM_STEP`，密集小事件叠加起来就是
 * "轻轻一捏就跳变很大"；换成随 `deltaY` 实际幅度连续换算倍率后，
 * 小捏合只贡献一点点，跟手指的实际移动量成比例。
 */
const WHEEL_DELTA_PER_STEP = 100;
const WHEEL_ZOOM_SENSITIVITY = Math.log(ZOOM_STEP) / WHEEL_DELTA_PER_STEP;

export interface KeyInput {
  readonly key: string;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly shiftKey?: boolean;
}

/**
 * 键盘 → 视图命令（§7.4 跟随 Photoshop）。
 *
 * macOS 上用 ⌘、其它平台用 Ctrl——两者在这里一视同仁，
 * 因为 Photoshop 的快捷键表本身就是这样跨平台对应的。
 */
export function viewCommandForKey(input: KeyInput): ViewCommand | null {
  const mod = input.ctrlKey === true || input.metaKey === true;
  if (!mod) return null;

  switch (input.key) {
    case '0':
      return { kind: 'fit' };
    case '1':
      return { kind: 'actualPixels' };
    case '+':
    case '=': // 主键盘上 + 要按 Shift，浏览器给的 key 常是 '='
      return { kind: 'zoomIn' };
    case '-':
    case '_':
      return { kind: 'zoomOut' };
    default:
      return null;
  }
}

export interface WheelInput {
  readonly deltaX: number;
  readonly deltaY: number;
  readonly altKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly shiftKey?: boolean;
}

/**
 * 滚轮 → 视图命令。
 *
 * Photoshop 的约定：**Alt+滚轮缩放**，裸滚轮平移。另外浏览器把触控板的
 * 双指捏合报成 `ctrlKey` 为真的 wheel 事件，那也应当是缩放——
 * 不认这一条的话，触控板用户完全无法缩放。
 */
export function viewCommandForWheel(input: WheelInput, anchor: ScreenPoint): ViewCommand {
  const zooming = input.altKey === true || input.ctrlKey === true || input.metaKey === true;
  if (zooming) {
    // deltaY 向下为正，而向下滚/捏合收拢应当缩小——取负再指数换算成连续倍率
    const factor = Math.exp(-input.deltaY * WHEEL_ZOOM_SENSITIVITY);
    return { kind: 'zoomBy', factor, anchor };
  }
  // Shift+滚轮 横向平移，跟随浏览器与 Photoshop 的共同惯例
  if (input.shiftKey === true && input.deltaX === 0) {
    return { kind: 'pan', dx: negate(input.deltaY), dy: 0 };
  }
  return { kind: 'pan', dx: negate(input.deltaX), dy: negate(input.deltaY) };
}

/**
 * 取负，且把 -0 归一成 0。
 *
 * `-0` 在算术上等于 0，但 `Object.is` 与深比较会把两者当成不同的值——
 * 一个纯粹由 `deltaX: 0` 取负而来的 -0 会让下游的相等判断莫名其妙地失败。
 */
function negate(value: number): number {
  return value === 0 ? 0 : -value;
}

export interface ApplyContext {
  readonly documentSize: Size;
  readonly viewSize: Size;
}

/** 把视图命令作用到视口上。纯函数——同样的输入永远得到同样的视口。 */
export function applyViewCommand(
  viewport: Viewport,
  command: ViewCommand,
  context: ApplyContext,
): Viewport {
  const center: ScreenPoint = { x: context.viewSize.width / 2, y: context.viewSize.height / 2 };

  switch (command.kind) {
    case 'fit':
      return fitToWindow(context.documentSize, context.viewSize);
    case 'actualPixels':
      return actualPixels(viewport, context.viewSize);
    case 'zoomIn':
      return zoomByFactor(viewport, command.anchor ?? center, ZOOM_STEP);
    case 'zoomOut':
      return zoomByFactor(viewport, command.anchor ?? center, 1 / ZOOM_STEP);
    case 'zoomBy':
      return zoomByFactor(viewport, command.anchor ?? center, command.factor);
    case 'pan':
      return panBy(viewport, command.dx, command.dy);
    case 'rotate':
      return rotateAt(viewport, command.anchor ?? center, viewport.rotation + command.radians);
    case 'resetRotation':
      return rotateAt(viewport, center, 0);
  }
}

/* ------------------------------------------------------------------ */
/* 抓手状态机                                                           */
/* ------------------------------------------------------------------ */

/**
 * 空格临时抓手。
 *
 * 两条容易被忽略的规则：
 * 1. **拖拽过程中松开空格，本次拖拽要继续到 pointerup**。中途切回原工具
 *    会让画布突然停住，而用户的手还在移动。
 * 2. 空格弹起时若没在拖拽，立刻回到原工具。
 */
export class HandTool {
  #spaceDown = false;
  #dragging = false;
  #last: ScreenPoint | null = null;

  /** 是否应当显示抓手光标。 */
  get active(): boolean {
    return this.#spaceDown || this.#dragging;
  }

  get dragging(): boolean {
    return this.#dragging;
  }

  setSpace(down: boolean): void {
    this.#spaceDown = down;
    // 拖拽中松开空格不打断本次拖拽——见类注释第 1 条
  }

  /** @returns 是否接管了这次按下。 */
  pointerDown(point: ScreenPoint): boolean {
    if (!this.#spaceDown) return false;
    this.#dragging = true;
    this.#last = point;
    return true;
  }

  /** @returns 需要施加的平移量；未在拖拽时返回 null。 */
  pointerMove(point: ScreenPoint): { dx: number; dy: number } | null {
    if (!this.#dragging || this.#last === null) return null;
    const delta = { dx: point.x - this.#last.x, dy: point.y - this.#last.y };
    this.#last = point;
    return delta;
  }

  pointerUp(): void {
    this.#dragging = false;
    this.#last = null;
  }
}
